import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import SEO from '../../components/SEO';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { supportAPI } from '../../services/supportAPI';
import { Search, Filter, MessageSquare, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
    'Open': 'badge-info',
    'Assigned': 'badge-neutral',
    'In Progress': 'badge-warning',
    'Waiting for Customer': 'badge-warning',
    'Resolved': 'badge-success',
    'Closed': 'badge-neutral',
    'Reopened': 'badge-danger',
};

const SupportTickets = () => {
    const { admin } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: '',
        category: '',
        search: ''
    });

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await supportAPI.getTickets(filters);
            setTickets(res.data.tickets);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, [filters.status, filters.category]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchTickets();
    };

    const isSuperAdmin = admin?.role === 'super_admin';
    const basePath = isSuperAdmin ? '/superadmin/support' : '/admin/support';

    return (
        <AdminLayout>
            <SEO title="Support Tickets | Admin" />
            
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-display font-bold text-midnight-900">Support Tickets</h1>
                        <p className="text-midnight-500 text-sm mt-1">Manage customer queries and issues</p>
                    </div>
                </div>

                <div className="card mb-6">
                    <div className="p-4 border-b border-midnight-100 bg-midnight-50/50 flex flex-wrap gap-4 items-center justify-between">
                        <form onSubmit={handleSearch} className="flex-1 min-w-[250px] relative">
                            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-midnight-400" />
                            <input 
                                type="text"
                                placeholder="Search by ticket #, name, email or booking ID"
                                className="input-field pl-10 py-2 text-sm"
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            />
                        </form>
                        
                        <div className="flex gap-4">
                            <select 
                                className="input-field py-2 text-sm w-40"
                                value={filters.status}
                                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="">All Statuses</option>
                                <option value="Open">Open</option>
                                <option value="Assigned">Assigned</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Waiting for Customer">Waiting for Customer</option>
                                <option value="Resolved">Resolved</option>
                                <option value="Closed">Closed</option>
                                <option value="Reopened">Reopened</option>
                            </select>

                            <select 
                                className="input-field py-2 text-sm w-48"
                                value={filters.category}
                                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                            >
                                <option value="">All Categories</option>
                                <option value="Booking Issue">Booking Issue</option>
                                <option value="Payment Issue">Payment Issue</option>
                                <option value="Refund Request">Refund Request</option>
                                <option value="Room Allocation">Room Allocation</option>
                                <option value="Room Cleanliness">Room Cleanliness</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex justify-center items-center py-20"><LoadingSpinner /></div>
                        ) : tickets.length === 0 ? (
                            <div className="text-center py-16">
                                <MessageSquare className="w-12 h-12 text-midnight-200 mx-auto mb-4" />
                                <h3 className="text-lg font-bold text-midnight-900 mb-1">No tickets found</h3>
                                <p className="text-midnight-500 text-sm">Adjust your filters to see more results</p>
                            </div>
                        ) : (
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-midnight-50 text-midnight-600 text-sm font-semibold border-b border-midnight-100">
                                        <th className="p-4">Ticket</th>
                                        <th className="p-4">Customer</th>
                                        <th className="p-4">Category</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4">Created</th>
                                        <th className="p-4 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.map(t => (
                                        <tr key={t._id} className="border-b border-midnight-100 hover:bg-midnight-50/50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-medium text-midnight-900">{t.ticketNumber}</div>
                                                <div className="text-xs text-midnight-500 truncate max-w-[200px]">{t.subject}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-sm font-medium">{t.customerName}</div>
                                                <div className="text-xs text-midnight-500">{t.email}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-sm">{t.category}</div>
                                                {isSuperAdmin && t.station && <div className="text-xs text-midnight-500">{t.station.name}</div>}
                                            </td>
                                            <td className="p-4">
                                                <span className={STATUS_COLORS[t.status] || 'badge-neutral'}>{t.status}</span>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-sm">{new Date(t.createdAt).toLocaleDateString()}</div>
                                                <div className="text-xs text-midnight-500">{new Date(t.createdAt).toLocaleTimeString()}</div>
                                            </td>
                                            <td className="p-4 text-right">
                                                <Link to={`${basePath}/${t._id}`} className="text-railway-600 hover:text-railway-700 text-sm font-medium">
                                                    View Details
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

            </div>
        </AdminLayout>
    );
};

export default SupportTickets;
